import React from 'react';
import {
  Paper,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { Metric, metricGroupedCategories } from '@features/deals/types';

interface FinancialPerformanceTableProps {
  metrics: Metric[];
  years: number[];
}

const FinancialPerformanceTable: React.FC<FinancialPerformanceTableProps> = ({
  metrics,
  years
}) => {
  // Helper function to format currency
  const formatCurrency = (value: number | string | null | undefined): string => {
    if (value === null || value === undefined) return '-';
    const numValue = typeof value === 'string' ? Number(value.replace(/[^0-9.-]/g, '')) : value;
    if (isNaN(numValue)) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(numValue);
  };

  // Group metrics by category
  const groupMetricsByCategory = () => {
    return metrics.reduce<Record<string, Metric[]>>((acc, metric) => {
      // Extract main category from metric
      let mainCategory = metric.category;
      if (metric.category.includes(' - ')) {
        mainCategory = metric.category.split(' - ')[0];
      }

      // Map to standard categories if possible
      Object.entries(metricGroupedCategories).forEach(([category]) => {
        if (mainCategory.toLowerCase().includes(category.toLowerCase())) {
          mainCategory = category;
        }
      });

      if (!acc[mainCategory]) {
        acc[mainCategory] = [];
      }
      acc[mainCategory].push(metric);
      return acc;
    }, {});
  };

  // Generate rows for financial table
  const generateTableRows = () => {
    const groupedMetrics = groupMetricsByCategory();

    return Object.entries(metricGroupedCategories)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([category, { items }]) => {
        const categoryMetrics = groupedMetrics[category] || [];
        const rows: React.ReactElement[] = [];
        let hasAddedRow = false;

        // Add individual item rows
        items.forEach((item) => {
          const itemMetrics = categoryMetrics.filter(m => {
            // Match by exact item name
            if (m.item === item) return true;
            // Match by category name with substring check
            if (m.category.toLowerCase().includes(item.toLowerCase())) return true;
            // Match by combined category-item
            if (m.category === `${category} - ${item}`) return true;
            return false;
          });

          // Always show total rows, or show item rows when they have data
          const shouldShowItem = item.includes('Total') || itemMetrics.length > 0;

          if (shouldShowItem) {
            hasAddedRow = true;
            rows.push(
              <TableRow
                key={`${category}-${item}`}
                sx={{
                  '&:nth-of-type(odd)': { backgroundColor: '#f5f5f5' },
                  '&:hover': { backgroundColor: '#f0f0f0' },
                  '&:last-child td, &:last-child th': { border: 0 },
                  ...(item.includes('Total') && {
                    backgroundColor: '#e3f2fd',
                    '&:hover': { backgroundColor: '#bbdefb' }
                  })
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: item.includes('Total') ? 600 : 500,
                    fontSize: '0.875rem',
                    color: '#424242'
                  }}
                >
                  {category}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.875rem',
                    color: '#424242',
                    fontWeight: item.includes('Total') ? 600 : 400
                  }}
                >
                  {item}
                </TableCell>
                {years.map((year) => {
                  let value: number | null = null;

                  if (item.includes('Total')) {
                    // Calculate total for the category
                    value = categoryMetrics
                      .filter(m => m.year === year)
                      .reduce((sum, m) => {
                        const val = typeof m.value === 'string' ?
                          Number(m.value.replace(/[^0-9.-]/g, '')) :
                          m.value;
                        return sum + (isNaN(Number(val)) ? 0 : Number(val));
                      }, 0);
                  } else {
                    // Get individual item value
                    const metric = itemMetrics.find(m => m.year === year);
                    if (metric) {
                      value = typeof metric.value === 'string' ?
                        Number(metric.value.replace(/[^0-9.-]/g, '')) :
                        metric.value;
                    }
                  }

                  return (
                    <TableCell
                      key={year}
                      align="right"
                      sx={{
                        fontFamily: '"Roboto Mono", monospace',
                        fontSize: '0.875rem',
                        fontWeight: item.includes('Total') ? 600 : 400,
                        color: '#000000'
                      }}
                    >
                      {formatCurrency(value)}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
          }
        });

        // If no rows were added for this category, add the total row
        if (!hasAddedRow) {
          // Find the Total item for this category
          const totalItem = items.find(item => item.includes('Total'));
          if (totalItem) {
            rows.push(
              <TableRow
                key={`${category}-${totalItem}`}
                sx={{
                  backgroundColor: '#e3f2fd',
                  '&:hover': { backgroundColor: '#bbdefb' },
                  '&:last-child td, &:last-child th': { border: 0 }
                }}
              >
                <TableCell
                  sx={{
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    color: '#424242'
                  }}
                >
                  {category}
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: '0.875rem',
                    color: '#424242',
                    fontWeight: 600
                  }}
                >
                  {totalItem}
                </TableCell>
                {years.map(year => (
                  <TableCell
                    key={year}
                    align="right"
                    sx={{
                      fontFamily: '"Roboto Mono", monospace',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      color: '#000000'
                    }}
                  >
                    {formatCurrency(0)}
                  </TableCell>
                ))}
              </TableRow>
            );
          }
        }

        return rows;
      }).flat();
  };

  return (
    <Paper
      sx={{
        p: 3,
        borderRadius: 2,
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: 'divider',
        backgroundColor: '#ffffff'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <MonetizationOnIcon sx={{ mr: 1, color: '#1a237e' }} />
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
          Financial Performance
        </Typography>
      </Box>
      {metrics.length > 0 ? (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: '#1a237e' }}>
                <TableCell
                  sx={{
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  Category
                </TableCell>
                <TableCell
                  sx={{
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem'
                  }}
                >
                  Item
                </TableCell>
                {years.map((year) => (
                  <TableCell
                    key={year}
                    align="right"
                    sx={{
                      color: '#ffffff',
                      fontWeight: 600,
                      fontSize: '0.875rem'
                    }}
                  >
                    {year}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {generateTableRows()}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" sx={{ color: '#424242' }}>
            No financial data available
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default FinancialPerformanceTable;